import * as React from "react";
import type { FC } from "react";
import { styled } from "@mui/material/styles";
import { Children, memo } from "react";
import {
    AppBar as MuiAppBar,
    type AppBarProps as MuiAppBarProps,
    Toolbar,
    useMediaQuery,
    type Theme,
    LinearProgress,
} from "@mui/material";
import { useLoading, useLocales } from "ra-core";
import { HideOnScroll, LoadingIndicator, LocalesMenuButton, TitlePortal, ToggleThemeButton, UserMenu, useThemesContext } from "react-admin";

/**
 * The AppBar component renders a custom MuiAppBar.
 *
 * @param {Object} props
 * @param {ReactNode} props.children React node/s to be rendered as children of the AppBar
 * @param {string} props.className CSS class applied to the MuiAppBar component
 * @param {string} props.color The color of the AppBar
 * @param {Element | boolean} props.userMenu A custom user menu component for the AppBar. <UserMenu/> component by default. Pass false to disable.
 *
 * @example // add a custom button to the AppBar
 *
 * const MyAppBar = () => (
 *   <AppBar>
 *     <TitlePortal />
 *     <MyCustomButton />
 *   </AppBar>
 * );
 *
 * @example // without a user menu
 *
 * const MyAppBar = () => <AppBar userMenu={false} />;
 */
export const AppBar: FC<AppBarProps> = memo(props => {
    const {
        alwaysOn,
        children,
        className,
        color = "secondary",
        toolbar = defaultToolbarElement,
        userMenu = DefaultUserMenu,
        container: Container = alwaysOn ? "div" : HideOnScroll,
        ...rest
    } = props;
    const loading = useLoading();
    const isXSmall = useMediaQuery<Theme>(theme =>
        theme.breakpoints.down("sm")
    );
    return (
        <Container className={className}>
            <StyledAppBar
                className={AppBarClasses.appBar}
                color={color}
                {...rest}
            >
                <Toolbar
                    disableGutters
                    variant={isXSmall ? "regular" : "dense"}
                    className={AppBarClasses.toolbar}
                >
                    {Children.count(children) === 0 ? (
                        <TitlePortal className={AppBarClasses.title} />
                    ) : (
                        children
                    )}
                    {typeof userMenu === "boolean" ? (
                        userMenu === true ? (
                            <UserMenu />
                        ) : null
                    ) : (
                        userMenu
                    )}
                    {toolbar}
                </Toolbar>
                <LinearProgress
                    color="secondary"
                    aria-label="Progress Bar"
                    sx={{ 
                        height: "1px", 
                        opacity: loading ? 1 : 0,
                        marginTop: "-3px"
                    }}
                />
            </StyledAppBar>
        </Container>
    );
});

const DefaultToolbar = () => {
    const locales = useLocales();
    const { darkTheme } = useThemesContext();
    return (
        <>
            {locales && locales.length > 1 ? <LocalesMenuButton /> : null}
            {darkTheme && <ToggleThemeButton />}
            <LoadingIndicator />
        </>
    );
};

const defaultToolbarElement = <DefaultToolbar />;

const DefaultUserMenu = <UserMenu />;

export interface AppBarProps extends MuiAppBarProps {
    /**
     * This prop is injected by Layout. You should not use it directly unless
     * you are using a custom layout.
     * If you are using the default layout, use `<Layout appBarAlwaysOn>` instead.
     */
    alwaysOn?: boolean;
    container?: React.ElementType<any>;
    toolbar?: JSX.Element;
    userMenu?: JSX.Element | boolean;
}

const PREFIX = "RaAppBar";

const AppBarClasses = {
    appBar: `${PREFIX}-appBar`,
    toolbar: `${PREFIX}-toolbar`,
    menuButton: `${PREFIX}-menuButton`,
    menuButtonIconClosed: `${PREFIX}-menuButtonIconClosed`,
    menuButtonIconOpen: `${PREFIX}-menuButtonIconOpen`,
    title: `${PREFIX}-title`,
};

const StyledAppBar = styled(MuiAppBar, {
    name: PREFIX,
    overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
    [`& .${AppBarClasses.toolbar}`]: {
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(0.25),
        [theme.breakpoints.down("md")]: {
            minHeight: theme.spacing(6),
        },
    },
    [`& .${AppBarClasses.menuButton}`]: {
        marginRight: "0.2em",
    },
    [`& .${AppBarClasses.title}`]: {},
}));
